import {
  useEvaluateInterviewMutation,
  useGetInterviewDetailsQuery,
} from '@/app/features/recruitment/evaluation/evaluation.api';
import type { InterviewQuestion } from '@/app/features/recruitment/evaluation/evaluation.type';
import { cn } from "@/lib/cn";
import CustomDrawer from '@/components/CustomDrawer';
import type { CustomModalProps } from '@/components/CustomModal';
import WithLoader from '@/components/WithLoader';
import { getErrorMessage } from '@/lib/error';
import { toast } from '@/lib/toast';
import { formatDate } from '@/utils';
import { Button, Divider, Flex, Form, Input, InputNumber } from 'antd';
import { useEffect } from 'react';

type Props = CustomModalProps & {
  id?: string;
  applicantName?: string;
  isEvaluated?: boolean;
};

export default function EvaluateManualInterview({
  handleClose,
  open,
  id,
  applicantName,
  isEvaluated,
}: Props) {
  const [form] = Form.useForm();

  const {
    isLoading,
    isFetching,
    data: detailsData,
  } = useGetInterviewDetailsQuery(id!, {
    skip: !open || !id,
  });

  const [evaluateInterview, { isLoading: submitting }] =
    useEvaluateInterviewMutation();

  const interviewDetails = detailsData?.data;

  const totalObtained = (interviewDetails?.questions ?? []).reduce(
    (sum: number, q: InterviewQuestion) => sum + (Number(q.obtained_mark) || 0),
    0,
  );
  const totalFull = (interviewDetails?.questions ?? []).reduce(
    (sum: number, q: InterviewQuestion) => sum + (Number(q.mark) || 100),
    0,
  );
  const percentage =
    totalFull > 0 ? Math.round((totalObtained / totalFull) * 100) : 0;

  // Pre-fill form with existing marks/comments when drawer opens
  useEffect(() => {
    if (open && interviewDetails?.questions) {
      form.setFieldsValue({
        questions: interviewDetails.questions.map((q: InterviewQuestion) => ({
          question_id: q.id,
          obtained_mark: q.obtained_mark,
          comment: q.comment,
          // Display fields (read-only)
          question: q.question,
          mark: q.mark,
          candidate_answer: q.candidate_answer,
          answer: q.answer,
        })),
      });
    }
  }, [open, interviewDetails, form]);

  const handleSubmit = async (values: any) => {
    try {
      const payload = {
        marks: values.questions.map((q: any) => ({
          question_id: q.question_id,
          obtained_mark: q.obtained_mark,
          comment: q.comment,
        })),
      };

      const res = await evaluateInterview({
        interview_id: id!,
        body: payload,
      }).unwrap();

      if (res?.success === false) {
        toast('error', getErrorMessage(res, 'Failed to submit evaluation'));
        return;
      }
      toast(
        'success',
        getErrorMessage(res, 'Evaluation submitted successfully'),
      );
      form.resetFields();
      handleClose();
    } catch (error) {
      toast('error', getErrorMessage(error, 'Failed to submit evaluation'));
    }
  };

  return (
    <CustomDrawer
      width={850}
      open={open}
      onClose={() => {
        form.resetFields();
        handleClose();
      }}
      title={
        isEvaluated
          ? `${applicantName || 'Applicant'}'s Marks' Breakdown`
          : 'Manual Interview Evaluation'
      }
    >
      <WithLoader isLoading={isLoading || isFetching}
        className={cn(
          "rounded-lg",
          (isLoading || isFetching) && "h-[calc(100vh_-_105px)]",
        )}>
        <Form
          form={form}
          size="large"
          layout="vertical"
          className="custom-form space-y-6 min-h-[400px]"
          onFinish={handleSubmit}
          disabled={isEvaluated}
        >
          {/* Applicant info header */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div>
              <h6 className="text-white/70 text-[17px]">
                Assigned Interviewer
              </h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.evaluator_name || 'N/A'}
              </h4>
            </div>
            <div>
              <h6 className="text-white/70 text-[17px]">Applicant</h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.job_application?.name || 'N/A'}
              </h4>
            </div>
            <div>
              <h6 className="text-white/70 text-[17px]">Interview Date</h6>
              <h4 className="text-xl font-semibold text-white">
                {interviewDetails?.interview_date
                  ? formatDate(interviewDetails.interview_date, 'DD/MM/YYYY')
                  : 'N/A'}{' '}
                {interviewDetails?.interview_time && (
                  <span className="text-base font-normal text-white/60">
                    {interviewDetails.interview_time}
                  </span>
                )}
              </h4>
            </div>
          </div>

          {isEvaluated && (
            <div className="mt-4 flex items-center justify-between">
              <h6 className="text-theme-primary text-[20px] font-medium mb-2">
                Total Score
              </h6>
              <span
                className={`px-4 py-1.5 rounded-full text-xl font-semibold ${
                  percentage >= 60
                    ? 'bg-theme-primary/20 text-theme-primary'
                    : 'bg-rose-500/20 text-rose-400'
                }`}
              >
                {percentage}%
              </span>
            </div>
          )}

          <Divider />

          {/* Question list */}
          <Form.List name="questions">
            {(fields) => (
              <div className="space-y-0">
                {fields.map((field) => {
                  const questionData = form.getFieldValue([
                    'questions',
                    field.name,
                  ]);
                  return (
                    <div
                      key={field.key}
                      className="border-b border-b-white/10 pb-8 pt-6 last:border-0 last:pb-0"
                    >
                      {/* Hidden question_id */}
                      <Form.Item name={[field.name, 'question_id']} hidden>
                        <InputNumber />
                      </Form.Item>

                      <Flex gap={24} justify="space-between" align="start">
                        {/* Question text */}
                        <div className="!w-full !grow">
                          <label className="text-white/60 block mb-2 text-sm uppercase tracking-wider font-bold">
                            Question {field.name + 1}
                          </label>
                          <span className="text-lg text-white font-semibold">
                            {questionData?.question ||
                              `Question ${field.name + 1}`}
                          </span>
                        </div>

                        {/* Full Mark */}
                        <div className="w-24 shrink-0 mt-2.5">
                          <label className="text-white block mb-2 text-sm tracking-wider">
                            Full Mark
                          </label>
                          <span className="text-lg text-white block font-bold">
                            {questionData?.mark ?? 100}
                          </span>
                        </div>

                        {/* Obtained Mark */}
                        {isEvaluated ? (
                          <div className="w-24 shrink-0 text-right">
                            <label className="text-white block mb-2 text-sm mt-2.5 tracking-wider">
                              Mark
                            </label>
                            <span className="text-theme-primary text-xl block font-bold">
                              {questionData?.obtained_mark ?? '—'}
                            </span>
                          </div>
                        ) : (
                          <Form.Item
                            label="Mark"
                            name={[field.name, 'obtained_mark']}
                            className="w-24 shrink-0 mb-0"
                            rules={[
                              { required: true, message: 'Required' },
                              {
                                type: 'number',
                                min: 0,
                                max: questionData?.mark ?? 0,
                                message: `0-${questionData?.mark ?? 0}`,
                              },
                            ]}
                          >
                            <InputNumber
                              className="w-full"
                              placeholder="0"
                              controls={false}
                              min={0}
                              max={questionData?.mark ?? 0}
                            />
                          </Form.Item>
                        )}
                      </Flex>

                      <div className="mt-6 space-y-6">
                        {/* <div className="">
                          <h6 className="text-[15px] mb-2 font-bold tracking-wide">
                            Applicant Answer
                          </h6>
                          <div className="p-4 rounded-xl border border-white/5 bg-white/[0.03] text-white/80 text-base leading-relaxed min-h-[80px]">
                            {questionData?.candidate_answer ||
                              'No answer provided'}
                          </div>
                        </div> */}

                        {/* Comment field */}
                        <Form.Item
                          label={
                            <span className="text-[15px] font-bold tracking-wide">
                              Interviewer Comment
                            </span>
                          }
                          name={[field.name, 'comment']}
                          className="mb-0"
                        >
                          {isEvaluated ? (
                            <div className="p-4 rounded-xl border border-white/5 bg-white/[0.03] text-white/80 text-base italic">
                              {questionData?.comment || 'No comment provided'}
                            </div>
                          ) : (
                            <Input.TextArea
                              rows={2}
                              placeholder="Add feedback for this answer..."
                              className="bg-white/[0.03] border-white/10 text-white"
                            />
                          )}
                        </Form.Item>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Form.List>

          {!isEvaluated && (
            <Flex align="center" justify="end" className="pt-6">
              <Button
                htmlType="submit"
                loading={submitting}
                size="large"
                className="btn-primary px-10 font-bold"
              >
                Submit Evaluation
              </Button>
            </Flex>
          )}
        </Form>
      </WithLoader>
    </CustomDrawer>
  );
}
